import { useState } from 'react'
import { Edit2, Trash2, GripVertical, Plus, Eye, EyeOff, ChevronDown, ChevronRight } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import { supabase } from '../../lib/supabase'
import ConfirmDialog from '../admin/ConfirmDialog'
import ContentViewer from './ContentViewer'

interface Module {
  id: string
  course_id: string
  title: string
  description: string | null
  order_index: number
  duration_minutes: number | null
  is_published: boolean
  lessons?: Lesson[]
}

interface Lesson {
  id: string
  module_id: string
  title: string
  content: string | null
  video_url: string | null
  order_index: number
  duration_minutes: number | null
  is_published: boolean
}

interface ModuleEditorProps {
  module: Module
  onEdit: (module: Module) => void
  onDelete: (moduleId: string) => void
  onTogglePublish: (moduleId: string, isPublished: boolean) => void
  onAddLesson: (moduleId: string) => void
  onEditLesson: (lesson: Lesson) => void
  onDeleteLesson: (lessonId: string) => void
  onReorderLessons: (moduleId: string, lessons: Lesson[]) => void
  dragHandleProps?: any
}

export default function ModuleEditor({
  module,
  onEdit,
  onDelete,
  onTogglePublish,
  onAddLesson,
  onEditLesson,
  onDeleteLesson,
  onReorderLessons,
  dragHandleProps
}: ModuleEditorProps) {
  const [expanded, setExpanded] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { showToast } = useToast()

  const handleTogglePublish = async () => {
    try {
      const { error } = await supabase
        .from('course_modules')
        .update({ is_published: !module.is_published })
        .eq('id', module.id)

      if (error) throw error

      onTogglePublish(module.id, !module.is_published)
      const message = module.is_published ? 'Módulo despublicado' : 'Módulo publicado'
      showToast('success', message)
    } catch (error: any) {
      console.error('Error toggling module publish:', error)
      showToast('error', 'Error al cambiar estado de publicación')
    }
  }

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('course_modules')
        .delete()
        .eq('id', module.id)

      if (error) throw error

      onDelete(module.id)
      showToast('success', 'Módulo eliminado exitosamente')
      setShowDeleteConfirm(false)
    } catch (error: any) {
      console.error('Error deleting module:', error)
      const errorMessage = 'Error al eliminar módulo: ' + error.message
      showToast('error', errorMessage)
    }
  }

  const lessonCount = module.lessons?.length || 0
  const publishedLessons = module.lessons?.filter(l => l.is_published).length || 0

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header del módulo */}
      <div className="flex items-center p-4 bg-gray-50 border-b border-gray-200">
        {/* Drag handle */}
        <div {...dragHandleProps} className="mr-3 cursor-grab active:cursor-grabbing">
          <GripVertical className="h-5 w-5 text-gray-400" />
        </div>

        {/* Expandir/Colapsar */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mr-3 text-gray-600 hover:text-education"
        >
          {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>

        {/* Información del módulo */}
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-900">{module.title}</h3>
            <span className="text-xs text-gray-500">
              {lessonCount} {lessonCount === 1 ? 'lección' : 'lecciones'}
            </span>
            {module.duration_minutes && (
              <span className="text-xs text-gray-500">
                {module.duration_minutes} min
              </span>
            )}
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              module.is_published
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {module.is_published ? 'Publicado' : 'Borrador'}
            </span>
          </div>
          {module.description && (
            <p className="text-sm text-gray-600 mt-1">{module.description}</p>
          )}
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={handleTogglePublish}
            className="p-2 text-gray-600 hover:text-education hover:bg-education/10 rounded"
            title={module.is_published ? 'Despublicar' : 'Publicar'}
          >
            {module.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
          <button
            onClick={() => onEdit(module)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
            title="Editar módulo"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-2 text-red-600 hover:bg-red-50 rounded"
            title="Eliminar módulo"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Lista de lecciones */}
      {expanded && (
        <div className="p-4 space-y-2">
          {module.lessons && module.lessons.length > 0 ? (
            <div className="space-y-2">
              {module.lessons
                .sort((a, b) => a.order_index - b.order_index)
                .map((lesson) => (
                  <LessonItem
                    key={lesson.id}
                    lesson={lesson}
                    onEdit={() => onEditLesson(lesson)}
                    onDelete={() => onDeleteLesson(lesson.id)}
                  />
                ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">
              No hay lecciones en este módulo
            </p>
          )}

          <button
            onClick={() => onAddLesson(module.id)}
            className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-education hover:text-education hover:bg-education/5 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Añadir Lección
          </button>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Eliminar Módulo"
        message={`¿Estás seguro de que quieres eliminar el módulo "${module.title}"? Esta acción eliminará también todas las lecciones asociadas.`}
        type="danger"
      />
    </div>
  )
}

interface LessonItemProps {
  lesson: Lesson
  onEdit: () => void
  onDelete: () => void
}

function LessonItem({ lesson, onEdit, onDelete }: LessonItemProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  return (
    <>
      <div className="flex items-start p-3 bg-gray-50 rounded border border-gray-200 hover:border-education hover:bg-education/5 transition-colors">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-900">{lesson.title}</span>
            {lesson.duration_minutes && (
              <span className="text-xs text-gray-500">{lesson.duration_minutes} min</span>
            )}
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              lesson.is_published
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {lesson.is_published ? 'Publicada' : 'Borrador'}
            </span>
          </div>
          <ContentViewer 
            content={lesson.content} 
            maxLength={150}
            className="text-xs text-gray-600 leading-relaxed"
            renderHtml={true}
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
            title="Editar lección"
          >
            <Edit2 className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
            title="Eliminar lección"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
        title="Eliminar Lección"
        message={`¿Estás seguro de que quieres eliminar la lección "${lesson.title}"?`}
        type="danger"
      />
    </>
  )
}
